import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Flex,
  Text,
  ActionButton,
  TooltipTrigger,
  Tooltip,
  Image
} from '@adobe/react-spectrum';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import { LegendBetaMiniIcon } from 'app/assets/legend/LegendBetaMiniIcon';
import { LegendPrivateBetaMiniIcon } from 'app/assets/legend/LegendPrivateBetaMiniIcon';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import {
  selectHighlightedIssue,
  selectCollapseAll
} from 'app/pages/Home/slice/selectors';
import {
  openDocument,
  isBetaLabel,
  isPrivateBetaLabel,
  isBetaOrPrivateBetaLabel,
  getReleasePhaseIcon
} from 'utils/helpers';
import alertTriangle from '../../../images/alert_traingle.svg';
import styles from './FeatureCard.module.scss';
import {
  normalizeRiskLevelToStored,
  RISK_LEVELS,
  STATUS_VALUES
} from 'utils/constants';
import { useSlideoutUrl } from 'app/hooks/useSlideoutUrl';
import { RiskLevelTooltipContent } from '../shared/RiskLevelTooltipContent';
import { ProductIconCompact } from '../TimelineCard/ProductIconCompact';
import { selectTimelineIconsVisible } from 'app/components/MonthlyTimeline/store/selectors';

export interface FeatureCardProps {
  type: string;
  card: {
    dates?: {
      targetStart?: string;
      targetEnd?: string;
      targetMonth?: string;
      targetQuarter?: string;
    };
    issueId: string;
    featureId?: string;
    title: string;
    categories?: string[];
    products?: string[]; // Add products array
    links?: Array<{ type: string; reference: string }>;
    riskLevel?: string;
    priority?: string;
    labels?: string[];
    status?: string;
    lastUpdated?: string;
    reportingStatus?: string;
    reportingSummary?: string;
    relationships?: Array<{ type: string; value: string; targetDate?: string }>;
    childRelationships?: any[];
    releasePhase?: string;
    // Feature-specific phase dates (e.g., GA)
    ga?: any;
    // Parent context for comparison/display
    parentReleasePhase?: string;
    parentDates?: any;
    parentShowOnTimeline?: boolean; // Flag to determine which parent dates to use
    // Name link - if present, clicking card opens this URL
    nameLink?: string;
    // RSD link - if present, clicking card opens this URL instead of slideout
    rsdLink?: string;
    // MVP flag
    mvp?: boolean;
  };
  isChild?: boolean;
  level?: number;
  parentPriority?: string;
  isQuarterExpanded?: boolean;
  isCompact?: boolean;
  isUnNested?: boolean;
  isSmall?: boolean;
  isLast?: boolean;
  /** Show success highlight after drag reorder (fits card width) */
  isJustReordered?: boolean;
  isMonthlyInline?: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  type,
  isLast = false,
  card,
  isChild = false,
  level = 0,
  parentPriority = '',
  isQuarterExpanded = true,
  isCompact = false,
  isUnNested = false,
  isJustReordered = false,
  isMonthlyInline = false
}) => {
  const {
    dates,
    issueId,
    featureId,
    title,
    categories,
    products, // Extract products from card
    links,
    riskLevel,
    priority,
    status,
    lastUpdated,
    reportingStatus,
    reportingSummary,
    relationships = [],
    childRelationships = [],
    releasePhase,
    ga,
    parentReleasePhase,
    parentDates,
    parentShowOnTimeline,
    nameLink,
    rsdLink,
    mvp
  } = card;

  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isClosed =
    status === STATUS_VALUES.COMPLETE || status === STATUS_VALUES.CLOSED;
  const isExpandable = childRelationships && childRelationships.length > 0;
  const timelineIconsVisible = useSelector(selectTimelineIconsVisible);
  const hideStatusIcons = !timelineIconsVisible && (isMonthlyInline || isChild);

  // Hook for slideout management
  const { openSlideout } = useSlideoutUrl();

  // Use featureId if issueId is not available (features use featureId)
  const actualIssueId = issueId || featureId;

  // Find parent issueId from relationships - check multiple possible relationship types
  const parentRelationship = relationships.find(rel => {
    const relType = rel.type?.toLowerCase() || '';
    return (
      relType === 'releases' ||
      relType === 'depends_on' ||
      relType === 'is child of' ||
      relType === 'parent' ||
      relType === 'belongs to'
    );
  });

  const parentIssueId = parentRelationship?.value;

  // Feature-specific overdue logic: use GA release date only
  const isFeatureCard = !!featureId || !!ga;

  const parseGaTokenToMoment = (val: string | null): moment.Moment | null => {
    if (!val) return null;
    const trimmed = val.trim();
    // ISO date yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return moment(trimmed, 'YYYY-MM-DD', true);
    }
    // Month formats like "Dec 2025" or "December 2025"
    const m1 = moment(trimmed, 'MMM YYYY', true);
    const m2 = moment(trimmed, 'MMMM YYYY', true);
    if (m1.isValid()) return m1.endOf('month');
    if (m2.isValid()) return m2.endOf('month');
    // Quarter format like "Q4 2025"
    const qMatch = /^Q([1-4])\s+(\d{4})$/i.exec(trimmed);
    if (qMatch) {
      const q = parseInt(qMatch[1], 10);
      const y = parseInt(qMatch[2], 10);
      // moment().quarter(q) sets quarter 1-4; start with first month then endOf('quarter')
      return moment({ year: y }).quarter(q).endOf('quarter');
    }
    return null;
  };

  const featureGaTokenForOverdue = getFeatureGaReleaseToken();
  const featureGaMoment = parseGaTokenToMoment(featureGaTokenForOverdue);
  const isOverdue = isFeatureCard
    ? !!featureGaMoment &&
      featureGaMoment.isBefore(moment(), 'day') &&
      !isClosed
    : dates?.targetEnd !== null
    ? dates?.targetEnd &&
      moment(dates.targetEnd).isBefore(moment(), 'day') &&
      !isClosed
    : dates?.targetStart &&
      moment(dates.targetStart).isBefore(moment(), 'day') &&
      !isClosed;

  const highlightedIssue = useSelector(selectHighlightedIssue);
  // Check both featureId and issueId for highlighting (features use featureId as primary identifier)
  const uniqueId = featureId || issueId;
  const isHighlighted = !!highlightedIssue[uniqueId];
  const isCollapsed = useSelector(selectCollapseAll);

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  const getStatusIcon = () => {
    const size = isMonthlyInline ? 16 : isCollapsed ? 20 : 22; // Small cards: 20px, Large cards: 22px (matching Figma designs)
    const normalizedRiskLevel = normalizeRiskLevelToStored(riskLevel);
    if (normalizedRiskLevel === RISK_LEVELS.LOW) {
      return <LegendRiskLowIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.MODERATE) {
      return <LegendRiskModerateIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.HIGH) {
      return <LegendRiskHighIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.IN_PLANNING) {
      return <LegendInPlanningIcon width={18} height={18} />;
    }
    return null;
  };

  // Helper to get display name for risk level
  const getRiskLevelDisplayName = (risk?: string): string => {
    if (!risk) return '';
    const normalized = risk.toLowerCase();
    if (normalized === 'green' || normalized === 'low') return 'Low';
    if (normalized === 'yellow' || normalized === 'moderate') return 'Moderate';
    if (normalized === 'red' || normalized === 'high') return 'High';
    if (normalized === 'in planning' || normalized === 'in_planning')
      return 'In Planning';
    return risk; // fallback to original value
  };

  const openDocumentLink = () => {
    // Priority 1: If nameLink is present, open it
    if (nameLink && nameLink.trim()) {
      window.open(nameLink, '_blank', 'noopener,noreferrer');
      return;
    }

    // Priority 2: If rsdLink is present, open it
    if (rsdLink && rsdLink.trim()) {
      window.open(rsdLink, '_blank', 'noopener,noreferrer');
      return;
    }

    // Priority 3: Open document link if available
    const docs = links?.filter(link => link.type === 'DOCUMENTATION');
    if (docs && docs.length > 0 && docs[0]?.reference) {
      openDocument(docs[0].reference);
    }
  };

  const docs = links?.filter(link => link.type === 'DOCUMENTATION') || [];

  // --- Feature GA badge logic (compare against parent release) ---
  const getParentReleaseToken = (): string | null => {
    const d = parentDates || {};
    let token: string | null = null;

    // If showOnTimeline is false (roadmap view), use target dates
    // If showOnTimeline is true (timeline view), use release dates
    if (parentShowOnTimeline === false) {
      // Roadmap view: use target/scheduled dates
      token = d.targetStart || d.targetMonth || d.targetQuarter || null;
    } else {
      // Timeline view (or default): use release/marketing dates
      token = d.releaseDate || d.releaseMonth || d.releaseQuarter || null;
    }

    if (!token) return null;
    return String(token);
  };

  function getFeatureGaReleaseToken(): string | null {
    if (!ga || !ga.date) return null;
    const dateObj = ga.date;
    if (typeof dateObj === 'string') return dateObj;
    const token =
      dateObj.releaseDate ||
      dateObj.releaseMonth ||
      dateObj.releaseQuarter ||
      null;
    if (!token) return null;
    return String(token);
  }

  const normalizeForCompare = (val: string | null): string | null => {
    if (!val) return null;
    return val.trim().toUpperCase();
  };

  const formatForDisplay = (val: string | null): string => {
    if (!val) return '';
    // If ISO date (yyyy-mm-dd), show DD MMM
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return moment(val).format('DD MMM').toUpperCase();
    }
    return val; // For Month/Quarter strings, show as is (e.g., "Dec 2025", "Q4 2025")
  };

  const effectiveParentPhase = parentReleasePhase || releasePhase;
  const parentReleaseToken = getParentReleaseToken();
  const featureGaToken = getFeatureGaReleaseToken();
  const showGaDateBadge =
    effectiveParentPhase === 'GA' &&
    !!featureGaToken &&
    !!parentReleaseToken &&
    normalizeForCompare(featureGaToken) !==
      normalizeForCompare(parentReleaseToken);
  const gaDateBadge = formatForDisplay(featureGaToken);

  return (
    <React.Fragment>
      <div
        ref={containerRef}
        data-testid={`feature-card-${uniqueId}`}
        className={`${styles.card} ${isClosed ? styles.closed : ''} ${
          isHighlighted ? styles.highlighted : ''
        } ${isJustReordered ? styles.justReordered : ''} ${
          isLast ? styles.lastCard : ''
        } ${isMonthlyInline ? styles.monthlyInline : ''}`}
        style={{
          marginLeft: isMonthlyInline ? '0' : '15px',
          cursor:
            (nameLink && nameLink.trim()) ||
            (rsdLink && rsdLink.trim()) ||
            docs.length > 0 ||
            parentIssueId
              ? 'pointer'
              : 'default'
        }}
        onClick={(e: React.MouseEvent) => {
          // Don't handle click if it's on the title (which has its own handler)
          if (
            (e.target as Element).closest(
              '[data-testid^="feature-card-title-"]'
            )
          ) {
            return;
          }

          // Priority 1: ALWAYS open parent moment slideout if parent exists
          if (parentIssueId && actualIssueId) {
            const productId =
              categories && categories.length > 0 ? categories[0] : undefined;
            const searchParams = new URLSearchParams();
            searchParams.set('featureId', actualIssueId);
            if (productId) {
              searchParams.set('productId', productId);
            }

            openSlideout(
              parentIssueId,
              undefined,
              undefined,
              searchParams.toString()
            );
            return;
          }

          // Fallback behaviors only if no parent exists
          if (nameLink && nameLink.trim()) {
            window.open(nameLink, '_blank', 'noopener,noreferrer');
          } else if (rsdLink && rsdLink.trim()) {
            window.open(rsdLink, '_blank', 'noopener,noreferrer');
          } else if (docs.length > 0) {
            openDocumentLink();
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.content}>
          <div className={styles.titleContainer}>
            <TooltipTrigger
              isOpen={openTooltip === 'title'}
              onOpenChange={isOpen => {
                if (!isOpen) setOpenTooltip(null);
              }}
              placement="top"
              offset={6}
            >
              <div
                onMouseEnter={() => handleTooltipMouseEnter('title')}
                onMouseLeave={handleTooltipMouseLeave}
                style={{ display: 'contents', width: '100%' }}
              >
                <ActionButton
                  data-testid={`feature-card-title-${featureId || issueId}`}
                  UNSAFE_className={styles.title}
                  onPress={() => {
                    // Priority 1: If parent exists, open parent slideout
                    if (parentIssueId && actualIssueId) {
                      const productId =
                        categories && categories.length > 0
                          ? categories[0]
                          : undefined;

                      console.log('🎯 FeatureCard opening slideout:', {
                        parentIssueId,
                        actualIssueId,
                        categories,
                        productId
                      });

                      const searchParams = new URLSearchParams();
                      searchParams.set('featureId', actualIssueId);
                      if (productId) {
                        searchParams.set('productId', productId);
                      }

                      console.log('📤 Query string:', searchParams.toString());

                      openSlideout(
                        parentIssueId,
                        undefined,
                        undefined,
                        searchParams.toString()
                      );
                    } else {
                      // Fallback: Open document link
                      openDocumentLink();
                    }
                  }}
                  isQuiet
                  UNSAFE_style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    width: 'fit-content'
                  }}
                >
                  <span className={styles.titleText}>{title}</span>
                </ActionButton>
              </div>
              <Tooltip>{title}</Tooltip>
            </TooltipTrigger>
          </div>

          <div className={styles.iconsContainer}>
            {/* Icon order from right: Status, Product, P/PB, GA date, MVP */}

            {/* GA date badge for features when different from parent GA */}
            {showGaDateBadge && (
              <TooltipTrigger placement="top">
                <ActionButton
                  isQuiet
                  UNSAFE_className={styles.gaBadgeButton}
                  data-testid={`feature-card-ga-badge-${featureId || issueId}`}
                  aria-label="Feature GA date"
                >
                  {gaDateBadge}
                </ActionButton>
                <Tooltip>Release Date</Tooltip>
              </TooltipTrigger>
            )}

            {/* MVP badge */}
            {mvp && (
              <span
                className={styles.priorityBadge}
                data-testid={`feature-card-mvp-badge-${featureId || issueId}`}
              >
                MVP
              </span>
            )}

            {/* Beta labels (P/PB icons) */}
            {releasePhase && getReleasePhaseIcon(releasePhase) && (
              <TooltipTrigger
                isOpen={
                  openTooltip === `label-${getReleasePhaseIcon(releasePhase)}`
                }
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() =>
                    handleTooltipMouseEnter(
                      `label-${getReleasePhaseIcon(releasePhase)}`
                    )
                  }
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    data-testid={`feature-card-label-${getReleasePhaseIcon(
                      releasePhase
                    )}-${issueId}`}
                    isQuiet
                    UNSAFE_className={styles.iconButton}
                    aria-label={releasePhase}
                  >
                    {getReleasePhaseIcon(releasePhase) === 'beta' ? (
                      <LegendBetaMiniIcon />
                    ) : (
                      <LegendPrivateBetaMiniIcon />
                    )}
                  </ActionButton>
                </div>
                <Tooltip>{releasePhase}</Tooltip>
              </TooltipTrigger>
            )}

            {/* Product Icon - shows first product with + indicator for multi-product */}
            {products && products.length > 0 && (
              <TooltipTrigger
                isOpen={openTooltip === 'product'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('product')}
                  onMouseLeave={handleTooltipMouseLeave}
                >
                  <ActionButton
                    isQuiet
                    UNSAFE_className={styles.iconButton}
                    aria-label="Product Icon"
                  >
                    <ProductIconCompact products={products} />
                  </ActionButton>
                </div>
                <Tooltip>
                  {Array.isArray(products)
                    ? products.length > 1
                      ? `${products[0]} +${products.length - 1} more`
                      : products[0]
                    : products}
                </Tooltip>
              </TooltipTrigger>
            )}

            {/* Risk level indicator (status) - rightmost */}
            {!isClosed && riskLevel && riskLevel !== 'NA' && (
              <div
                className={
                  hideStatusIcons ? styles.monthlyInlineIconHidden : ''
                }
                aria-hidden={hideStatusIcons}
              >
                <TooltipTrigger
                  isOpen={openTooltip === 'risk'}
                  onOpenChange={isOpen => {
                    if (!isOpen) setOpenTooltip(null);
                  }}
                  placement="top"
                >
                  <div
                    onMouseEnter={() => handleTooltipMouseEnter('risk')}
                    onMouseLeave={handleTooltipMouseLeave}
                  >
                    <ActionButton
                      data-testid={`feature-card-risk-level-${issueId}`}
                      isQuiet
                      UNSAFE_className={styles.iconButton}
                      aria-label={`Risk Level: ${getRiskLevelDisplayName(
                        riskLevel
                      )}`}
                    >
                      {getStatusIcon()}
                    </ActionButton>
                  </div>
                  <Tooltip>
                    <RiskLevelTooltipContent riskLevel={riskLevel} />
                  </Tooltip>
                </TooltipTrigger>
              </div>
            )}
          </div>
        </div>

        {/* Checkmark removed - no longer showing for completed cards */}
        {/* {isClosed && (
          <div
            className={styles.checkmark}
            data-testid={`feature-card-checkmark-${issueId}`}
          >
            <CheckMarkCircleIcon
              width={isCollapsed ? '12' : '14'}
              height={isCollapsed ? '12' : '14'}
            />
          </div>
        )} */}

        {/* Overdue indicator */}
        {isOverdue && !isMonthlyInline && (
          <div
            className={`${styles.overdueIndicator} ${
              hideStatusIcons ? styles.monthlyInlineIconHidden : ''
            }`}
            data-testid={`feature-card-overdue-${issueId}`}
            aria-hidden={hideStatusIcons}
          >
            <Image
              src={alertTriangle}
              alt="Overdue"
              width={isCollapsed ? '12px' : '14px'}
              height={isCollapsed ? '12px' : '14px'}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default FeatureCard;
